import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type AnnouncementSeverity = "info" | "success" | "warning";
export type AnnouncementAudience = "all" | "tier";
export type AccountTier = Database["public"]["Enums"]["account_tier"];

export type AdminAnnouncement = {
  id: string;
  created_by_user_id: string;
  title: string;
  body_md: string;
  video_url: string | null;
  severity: AnnouncementSeverity;
  audience: AnnouncementAudience;
  audience_tier: AccountTier | null;
  published_at: string | null;
  expires_at: string | null;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
};

export type AnnouncementRead = {
  id: string;
  announcement_id: string;
  user_id: string;
  read_at: string;
  dismissed_at: string | null;
};

export type AnnouncementInput = {
  title: string;
  body_md: string;
  video_url?: string | null;
  severity: AnnouncementSeverity;
  audience: AnnouncementAudience;
  audience_tier: AccountTier | null;
  is_pinned: boolean;
  published_at: string | null;
  expires_at: string | null;
};

export async function listAnnouncementsAdmin(): Promise<AdminAnnouncement[]> {
  const { data, error } = await supabase
    .from("admin_announcements")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as AdminAnnouncement[];
}

export async function listAnnouncementsForUser(): Promise<AdminAnnouncement[]> {
  const { data, error } = await supabase
    .from("admin_announcements")
    .select("*")
    .order("is_pinned", { ascending: false })
    .order("published_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as AdminAnnouncement[];
}

export async function listMyReads(): Promise<AnnouncementRead[]> {
  const { data, error } = await supabase
    .from("admin_announcement_reads")
    .select("*");
  if (error) throw error;
  return (data ?? []) as AnnouncementRead[];
}

export async function listReadsForAnnouncement(announcementId: string): Promise<AnnouncementRead[]> {
  const { data, error } = await supabase
    .from("admin_announcement_reads")
    .select("*")
    .eq("announcement_id", announcementId);
  if (error) throw error;
  return (data ?? []) as AnnouncementRead[];
}

export async function createAnnouncement(input: AnnouncementInput, userId: string): Promise<AdminAnnouncement> {
  const { data, error } = await supabase
    .from("admin_announcements")
    .insert({
      created_by_user_id: userId,
      title: input.title.trim(),
      body_md: input.body_md,
      video_url: input.video_url?.trim() || null,
      severity: input.severity,
      audience: input.audience,
      audience_tier: input.audience === "tier" ? input.audience_tier : null,
      is_pinned: input.is_pinned,
      published_at: input.published_at,
      expires_at: input.expires_at,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data as AdminAnnouncement;
}

type AnnouncementUpdate = Database["public"]["Tables"]["admin_announcements"]["Update"];

export async function updateAnnouncement(id: string, input: Partial<AnnouncementInput>): Promise<AdminAnnouncement> {
  const patch: AnnouncementUpdate = {};
  if (input.title !== undefined) patch.title = input.title.trim();
  if (input.body_md !== undefined) patch.body_md = input.body_md;
  if (input.video_url !== undefined) patch.video_url = input.video_url?.trim() || null;
  if (input.severity !== undefined) patch.severity = input.severity;
  if (input.audience !== undefined) patch.audience = input.audience;
  if (input.audience_tier !== undefined) patch.audience_tier = input.audience_tier;
  if (input.is_pinned !== undefined) patch.is_pinned = input.is_pinned;
  if (input.published_at !== undefined) patch.published_at = input.published_at;
  if (input.expires_at !== undefined) patch.expires_at = input.expires_at;
  const { data, error } = await supabase
    .from("admin_announcements")
    .update(patch)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data as AdminAnnouncement;
}

export async function deleteAnnouncement(id: string): Promise<void> {
  const { error } = await supabase.from("admin_announcements").delete().eq("id", id);
  if (error) throw error;
}

export async function publishAnnouncement(id: string): Promise<AdminAnnouncement> {
  return updateAnnouncement(id, { published_at: new Date().toISOString() });
}

export async function unpublishAnnouncement(id: string): Promise<AdminAnnouncement> {
  return updateAnnouncement(id, { published_at: null });
}

export async function markAnnouncementRead(announcementId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from("admin_announcement_reads")
    .upsert(
      { announcement_id: announcementId, user_id: userId, read_at: new Date().toISOString() },
      { onConflict: "announcement_id,user_id" },
    );
  if (error) throw error;
}

export async function dismissAnnouncement(announcementId: string, userId: string): Promise<void> {
  const nowIso = new Date().toISOString();
  const { error } = await supabase
    .from("admin_announcement_reads")
    .upsert(
      { announcement_id: announcementId, user_id: userId, read_at: nowIso, dismissed_at: nowIso },
      { onConflict: "announcement_id,user_id" },
    );
  if (error) throw error;
}

export type AnnouncementStatus = "draft" | "scheduled" | "live" | "expired";

export function getAnnouncementStatus(a: AdminAnnouncement, now = new Date()): AnnouncementStatus {
  if (!a.published_at) return "draft";
  const pub = new Date(a.published_at);
  if (pub > now) return "scheduled";
  if (a.expires_at && new Date(a.expires_at) <= now) return "expired";
  return "live";
}

/** Extracts a YouTube/Vimeo embed URL or returns the raw URL for video files. */
export function resolveVideoEmbed(url: string): { kind: "iframe" | "video"; src: string } | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = u.searchParams.get("v");
      if (id) return { kind: "iframe", src: `https://www.youtube.com/embed/${id}` };
    }
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      if (id) return { kind: "iframe", src: `https://www.youtube.com/embed/${id}` };
    }
    if (host === "vimeo.com") {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (id) return { kind: "iframe", src: `https://player.vimeo.com/video/${id}` };
    }
    if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(u.pathname)) {
      return { kind: "video", src: url };
    }
    return { kind: "iframe", src: url };
  } catch {
    return null;
  }
}
